import { useState } from 'react'

import {
  Card,
  CardContent,
  CardHeading,
  SegmentedControl,
  type SegmentedOption,
  StackedBarChart,
} from '@/components/ui'
import {
  chartPalette,
  numberToCompactCurrency,
  numberToCurrency,
} from '@/utils'
import { CaseActivityTimeline } from '../overview/CaseActivityTimeline'
import type { ActivityMonth } from '../overview/caseActivity'
import type { ActivityRow, RealizedRow } from './types'

type Lens = 'cashflow' | 'realized' | 'activity'

// Buy/sell legs move cash inside the account, not in or out of it. The cash-flow lens drops them
// so the bars show real deposits, withdrawals, transfers, income, and fees.
const TRADE_TYPES = new Set([
  'security_purchase',
  'security_sale',
  'reinvestment',
])

// How many activity types to color before the rest fold into "Other".
const MAX_TYPES = 5

// The realized lens relabels the up/down timeline as gains over losses. Activity types use palette
// hues that skip green and red so a category can't be misread as money in or out.
const REALIZED_LABELS = {
  inflow: 'Gains',
  outflow: 'Losses',
  inflowShort: 'Gains',
  outflowShort: 'Losses',
  net: 'Net',
}
const TYPE_PALETTE = [
  chartPalette[0], // blue
  chartPalette[5], // teal
  chartPalette[8], // violet
  chartPalette[4], // orange
  chartPalette[6], // pink
]
const OTHER_COLOR = chartPalette[chartPalette.length - 1] // gray

// The distinct statement periods present in the rows, oldest first, with their human labels.
const orderedPeriods = (rows: { period: string; periodLabel: string }[]) => {
  const labels = new Map<string, string>()
  for (const row of rows) labels.set(row.period, row.periodLabel)
  return Array.from(labels.entries())
    .sort(([first], [second]) => first.localeCompare(second))
    .map(([key, label]) => ({ key, label }))
}

// Shape each period into the timeline's month row: one bar up, one down. shortLabel is the period
// label itself since these are statement periods, not calendar months.
const toMonths = (
  periods: { key: string; label: string }[],
  inflowByPeriod: Map<string, number>,
  outflowByPeriod: Map<string, number>,
): ActivityMonth[] =>
  periods.map((period) => ({
    key: period.key,
    label: period.label,
    shortLabel: period.label,
    inflow: inflowByPeriod.get(period.key) ?? 0,
    outflow: outflowByPeriod.get(period.key) ?? 0,
  }))

export const cashFlow = (activity: ActivityRow[]) => {
  const external = activity.filter((row) => !TRADE_TYPES.has(row.type))
  const inByPeriod = new Map<string, number>()
  const outByPeriod = new Map<string, number>()
  let grossIn = 0
  let grossOut = 0
  for (const row of external) {
    const amount = row.amount ?? 0
    if (amount >= 0) {
      inByPeriod.set(row.period, (inByPeriod.get(row.period) ?? 0) + amount)
      grossIn += amount
    } else {
      const magnitude = Math.abs(amount)
      outByPeriod.set(
        row.period,
        (outByPeriod.get(row.period) ?? 0) + magnitude,
      )
      grossOut += magnitude
    }
  }
  const data = toMonths(orderedPeriods(external), inByPeriod, outByPeriod)
  return { data, grossIn, grossOut }
}

const realizedFlow = (realized: RealizedRow[]) => {
  const gainsByPeriod = new Map<string, number>()
  const lossesByPeriod = new Map<string, number>()
  let shortTerm = 0
  let longTerm = 0
  for (const row of realized) {
    const gainLoss = row.gainLoss ?? 0
    if (gainLoss >= 0) {
      gainsByPeriod.set(
        row.period,
        (gainsByPeriod.get(row.period) ?? 0) + gainLoss,
      )
    } else {
      lossesByPeriod.set(
        row.period,
        (lossesByPeriod.get(row.period) ?? 0) + Math.abs(gainLoss),
      )
    }
    if (/short/i.test(row.term ?? '')) shortTerm += gainLoss
    else if (/long/i.test(row.term ?? '')) longTerm += gainLoss
  }
  const data = toMonths(orderedPeriods(realized), gainsByPeriod, lossesByPeriod)
  return { data, shortTerm, longTerm }
}

const activityMix = (activity: ActivityRow[]) => {
  const volumeByType = new Map<string, number>()
  for (const row of activity) {
    const type = row.type || 'Other'
    volumeByType.set(
      type,
      (volumeByType.get(type) ?? 0) + Math.abs(row.amount ?? 0),
    )
  }
  const ranked = Array.from(volumeByType.keys()).sort(
    (first, second) =>
      (volumeByType.get(second) ?? 0) - (volumeByType.get(first) ?? 0),
  )
  const named = ranked.slice(0, MAX_TYPES)
  const hasOther = ranked.length > MAX_TYPES
  const bucketFor = (type: string) => (named.includes(type) ? type : 'Other')

  const data = orderedPeriods(activity).map((period) => {
    const point: Record<string, number | string> = {
      date: period.label,
    }
    for (const key of [...named, ...(hasOther ? ['Other'] : [])]) point[key] = 0
    for (const row of activity) {
      if (row.period !== period.key) continue
      const key = bucketFor(row.type || 'Other')
      point[key] = (point[key] as number) + Math.abs(row.amount ?? 0)
    }
    return point
  })

  const series = named.map((type, index) => ({
    key: type,
    label: type,
    color: TYPE_PALETTE[index % TYPE_PALETTE.length],
  }))
  if (hasOther)
    series.push({ key: 'Other', label: 'Other', color: OTHER_COLOR })

  return { data, series }
}

const LENSES: SegmentedOption<Lens>[] = [
  { value: 'cashflow', label: 'Cash flow' },
  { value: 'realized', label: 'Realized gains' },
  { value: 'activity', label: 'Activity mix' },
]

const Empty = ({ message }: { message: string }) => (
  <div className="flex h-full items-center justify-center text-sm text-ink-subtle">
    {message}
  </div>
)

interface PortfolioFlowsChartProps {
  activity: ActivityRow[]
  realized: RealizedRow[]
}

// The forensic companion to the value hero: where money came from and went, when gains were booked,
// and what the accounts were doing. One frame, three lenses.
export const PortfolioFlowsChart = ({
  activity,
  realized,
}: PortfolioFlowsChartProps) => {
  const [lens, setLens] = useState<Lens>('cashflow')

  const renderBody = () => {
    if (lens === 'cashflow') {
      const { data } = cashFlow(activity)
      if (data.length === 0)
        return <Empty message="No cash movement to plot." />
      return <CaseActivityTimeline months={data} />
    }
    if (lens === 'realized') {
      const { data } = realizedFlow(realized)
      if (data.length === 0)
        return <Empty message="No realized gains to plot." />
      return <CaseActivityTimeline months={data} labels={REALIZED_LABELS} />
    }
    const { data, series } = activityMix(activity)
    if (series.length === 0)
      return <Empty message="No activity to break down." />
    return (
      <StackedBarChart
        data={data}
        series={series}
        height={280}
        showLegend
        legendPosition="bottom"
        yTickFormatter={(value) => numberToCompactCurrency(Number(value))}
        marginLeft={56}
      />
    )
  }

  const caption = () => {
    if (lens === 'cashflow') {
      const { grossIn, grossOut } = cashFlow(activity)
      return `In ${numberToCompactCurrency(grossIn)}, out ${numberToCompactCurrency(grossOut)}, trades excluded`
    }
    if (lens === 'realized') {
      const { shortTerm, longTerm } = realizedFlow(realized)
      return `Short-term ${numberToCurrency(shortTerm)}, long-term ${numberToCurrency(longTerm)}`
    }
    return 'Dollar volume by activity type, per statement period.'
  }

  return (
    <Card className="w-full">
      <CardHeading
        className="flex-wrap"
        title="Money movement"
        description={caption()}
        actions={
          <SegmentedControl options={LENSES} value={lens} onChange={setLens} />
        }
      />
      <CardContent>
        <div className="h-[280px]">{renderBody()}</div>
      </CardContent>
    </Card>
  )
}
